/**
 * 신용정보원 비밀번호 만료 이벤트 버스 (armed-generation latch).
 *
 * shared/auth 는 router/UI 에 의존하면 안 되므로(FSD 의존성 방향),
 * 보험 재동기화 잡이 만료(CREDIT4U_REQUIRES_PASSWORD_RESET)로 실패하면
 * 여기서 이벤트만 emit 하고 features 계층
 * (useCredit4uPasswordExpiryRecovery)이 구독해 팝업·네비게이션을 처리한다.
 * (authSessionEvents.ts 와 동일한 shared 이벤트 버스 패턴.)
 *
 * ── armed-generation latch (중복 발화 차단) ──
 * 만료는 홈/Fees배너/온보딩 등 여러 소비처가 각자 폴링하다 동시에 감지할 수
 * 있고, 한 사용자 액션(새로고침 1회)이 여러 늦은 terminal emit 을 낼 수도 있다.
 * 팝업은 "사용자 명시 새로고침 1회당 최대 1회"여야 하므로, dedup 을 버스 계약에
 * 내장한다:
 *   - armCredit4uPasswordExpiry(): 새로고침 시작 시 소유자 1곳이 호출.
 *     세대(generation)를 올리고 latch 를 무장(consumed=false)한 뒤 gen 을 반환.
 *   - emitCredit4uPasswordExpired({ generation, errorMessage }): 만료 감지 시 호출.
 *     전달된 generation 이 현재 무장 세대와 같고 아직 소진되지 않았을 때만
 *     소진 처리 후 리스너에 통지. 이전 세대·소진 후 늦은 emit 은 무시된다.
 *   - 팝업이 열리면 그 세대 소진 → 다음 새로고침이 다시 arm 해야 재발화.
 */
export type Credit4uPasswordExpiredEvent = {
  /** arm 시 발급된 세대 번호 — emit 은 이 값이 현재 무장 세대와 일치할 때만 통지된다. */
  generation: number;
  errorMessage?: string | null;
};

const listeners = new Set<(event: Credit4uPasswordExpiredEvent) => void>();

// 현재 무장된 세대. arm 마다 증가한다.
let armedGeneration = 0;
// 현재 세대가 소진되었는지. 초기값 true(무장 전) — arm 만 false 로 바꾼다.
let consumed = true;

/**
 * 새로고침 시작 시 만료 감지를 무장한다. 세대를 올리고 latch 를 리셋(미소진)한 뒤
 * 세대 번호를 반환한다. 반환값을 이 새로고침 시도 동안 보관했다가 emit 에 그대로 넘긴다.
 * ⚠️ 한 사용자 액션 안에서 job 시작 책임자 1곳만 호출한다(중복 arm 금지).
 */
export const armCredit4uPasswordExpiry = (): number => {
  armedGeneration += 1;
  consumed = false;
  return armedGeneration;
};

/**
 * 만료 감지 시 호출한다. 전달 generation 이 현재 무장 세대와 같고 미소진일 때만
 * 소진 처리 후 리스너에 통지한다(그 외 무시 — 이전 세대·중복 burst·소진 후 늦은 emit 차단).
 */
export const emitCredit4uPasswordExpired = (
  event: Credit4uPasswordExpiredEvent,
): void => {
  if (event.generation !== armedGeneration || consumed) return;
  consumed = true;
  listeners.forEach((listener) => listener(event));
};

export const subscribeCredit4uPasswordExpired = (
  listener: (event: Credit4uPasswordExpiredEvent) => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
